import { Badge, BadgeDot } from "@lagoon/ui";

import type { ScreeningRelease } from "../api.js";

/**
 * The console's vocabulary of states, in one file.
 *
 * Colour is the fastest thing to read on a dense screen and the easiest to
 * make mean two different things on two different screens. Every state badge
 * in the console is built here so that red always means "this is off the air
 * or refused", green always means "this is serving", and nothing else gets to
 * borrow either.
 *
 * Every badge carries its own text. The dot and the tint are the second way of
 * saying it, never the only way, which is what keeps the screen readable for
 * somebody who cannot separate the two reds.
 */

/** Live, suspended, or built but never published. */
export function SiteStateBadge({
  suspendedAt,
  publishedDeployId,
  reason,
}: {
  suspendedAt: string | null;
  publishedDeployId: string | null;
  reason?: string | null;
}): React.JSX.Element {
  if (suspendedAt !== null) {
    return (
      <Badge tone="danger" {...(reason ? { title: reason } : {})}>
        <BadgeDot />
        Suspended
      </Badge>
    );
  }
  if (publishedDeployId !== null) {
    return (
      <Badge tone="success">
        <BadgeDot />
        Live
      </Badge>
    );
  }
  return <Badge tone="neutral">No release</Badge>;
}

/** An owner or an organisation: either it is frozen or it is not. */
export function AccountStateBadge({
  suspendedAt,
  reason,
}: {
  suspendedAt: string | null;
  reason?: string | null;
}): React.JSX.Element {
  return suspendedAt === null ? (
    <Badge tone="success">
      <BadgeDot />
      Active
    </Badge>
  ) : (
    <Badge tone="danger" {...(reason ? { title: reason } : {})}>
      <BadgeDot />
      Suspended
    </Badge>
  );
}

/**
 * The five scan states.
 *
 * `blocked` is red because a person refused the release; `error` is violet
 * because nothing was decided at all: the scanner fell over, and a release
 * nobody has judged is a different problem from one somebody has refused. They
 * are deliberately not the same colour.
 */
const SCAN_TONE = {
  pending: "neutral",
  clean: "success",
  flagged: "warning",
  blocked: "danger",
  error: "accent",
} as const;

export function ScanStateBadge({
  state,
}: {
  state: ScreeningRelease["scanState"];
}): React.JSX.Element {
  return (
    <Badge tone={SCAN_TONE[state]}>
      <BadgeDot />
      {state}
    </Badge>
  );
}

/**
 * What this operator may do, said out loud in the header.
 *
 * An auditor who cannot see why the buttons are missing files a bug; an
 * operator who thinks they are an administrator tries an action that will be
 * refused. The badge is the cheapest fix for both.
 */
export function RoleBadge({
  role,
  permissions,
}: {
  role: string;
  permissions: string[];
}): React.JSX.Element {
  const tone = role === "Admin" ? "primary" : role === "Operator" ? "info" : "neutral";
  return (
    <Badge tone={tone} title={permissions.join(", ")}>
      {role}
    </Badge>
  );
}
